import { useCallback, useState, useRef } from 'react';

export interface MutationState<TResult> {
  data?: TResult;
  error?: unknown;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  reset: () => void;
}

export function useMutation<TArg, TResult>(
  mutationFn: (arg: TArg) => Promise<TResult>
): [(arg: TArg) => Promise<TResult | undefined>, MutationState<TResult>] {
  const [data, setData] = useState<TResult | undefined>();
  const [error, setError] = useState<unknown>();
  const [isLoading, setIsLoading] = useState(false);
  const mutationFnRef = useRef(mutationFn);

  mutationFnRef.current = mutationFn;

  const mutate = useCallback(async (arg: TArg) => {
    setIsLoading(true);
    setError(undefined);

    try {
      const result = await mutationFnRef.current(arg);
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(undefined);
    setError(undefined);
    setIsLoading(false);
  }, []);

  return [
    mutate,
    {
      data,
      error,
      isLoading,
      isSuccess: !!data && !error,
      isError: !!error,
      reset,
    },
  ];
}
